import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "~/hooks";
import AppModal from "../modals/AppModal";
import "../style.css";

const SYSTEMS_CHIPS = [
  'ERP Mini', 'AI B2B', 'CRM', 'DMS', 'PM', 'HRM',
  'PMS', 'MES', 'TMS', 'WMS', 'EAM', 'APExLINK',
];

const TERMINAL_LINES = [
  { text: '$ apetechs login', delay: 0, color: '#50FA7B' },
  { text: '✓ Authenticated as admin@apetechs.com', delay: 600, color: '#8BE9FD' },
  { text: '', delay: 900 },
  { text: '$ apetechs systems list', delay: 1100, color: '#50FA7B' },
  { text: '  [01] ERP Mini        → /erpMini', delay: 1400, color: '#E2E8F0' },
  { text: '  [02] AI B2B Platform → platform.apetechs.co', delay: 1650, color: '#E2E8F0' },
  { text: '  [03] CRM             → /crm', delay: 1900, color: '#E2E8F0' },
  { text: '  [04] DMS             → /dms', delay: 2150, color: '#E2E8F0' },
  { text: '  [05] PM              → /pm', delay: 2400, color: '#E2E8F0' },
  { text: '  [06] HRM             → /hrm', delay: 2650, color: '#E2E8F0' },
  { text: '  [07] PMS             → /pms', delay: 2900, color: '#E2E8F0' },
  { text: '  [08] MES             → /mes', delay: 3150, color: '#E2E8F0' },
  { text: '  [09] TMS             → /tms', delay: 3400, color: '#E2E8F0' },
  { text: '  [10] WMS             → /wms', delay: 3650, color: '#E2E8F0' },
  { text: '  [11] EAM             → /eam', delay: 3900, color: '#E2E8F0' },
  { text: '  [12] APExLINK        → apexlink.dev', delay: 4150, color: '#FBAD16' },
  { text: '', delay: 4500 },
  { text: '$ apetechs stats --brief', delay: 4700, color: '#50FA7B' },
  { text: '  customers:  30+   systems: 20+', delay: 5100, color: '#BD93F9' },
  { text: '  projects:   60+   team:    60+', delay: 5400, color: '#BD93F9' },
  { text: '  uptime:     99.9% SLA ✓', delay: 5700, color: '#50FA7B' },
];

const TerminalWindow: React.FC = () => {
  const [visibleLines, setVisibleLines] = useState<number>(0);
  const [typingLine, setTypingLine] = useState<string>('');
  const [typingIndex, setTypingIndex] = useState<number>(0);
  const [typingActive, setTypingActive] = useState<boolean>(false);
  const termBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timeouts: ReturnType<typeof setTimeout>[] = [];

    TERMINAL_LINES.forEach((line, i) => {
      const t = setTimeout(() => {
        if (line.text.startsWith('$ ')) {
          setTypingLine('');
          setTypingIndex(i);
          setTypingActive(true);
        } else {
          setVisibleLines((v) => v + 1);
        }
      }, line.delay);
      timeouts.push(t);
    });

    return () => timeouts.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (!typingActive) return;
    const target = TERMINAL_LINES[typingIndex]?.text || '';
    let charIdx = 0;
    const iv = setInterval(() => {
      charIdx++;
      setTypingLine(target.slice(0, charIdx));
      if (charIdx >= target.length) {
        clearInterval(iv);
        setTypingActive(false);
        setVisibleLines((v) => v + 1);
        setTypingLine('');
      }
    }, 35);
    return () => clearInterval(iv);
  }, [typingActive, typingIndex]);

  useEffect(() => {
    if (termBodyRef.current) {
      termBodyRef.current.scrollTop = termBodyRef.current.scrollHeight;
    }
  }, [visibleLines, typingLine]);

  const renderedLines: React.ReactNode[] = [];
  let shown = 0;
  TERMINAL_LINES.forEach((line, i) => {
    if (i === typingIndex && typingLine !== '') {
      renderedLines.push(
        <div key={i} style={{ color: line.color || '#E2E8F0', minHeight: '1.4em' }}>
          {typingLine}
          <span style={{
            display: 'inline-block', width: 7, height: '1em',
            background: '#FBAD16', marginLeft: 1, verticalAlign: 'text-bottom',
            animation: 'devBlink 0.8s step-end infinite',
          }} />
        </div>
      );
    } else if (shown < visibleLines && i !== typingIndex) {
      shown++;
      renderedLines.push(
        <div key={i} style={{ color: line.color || '#E2E8F0', minHeight: line.text ? '1.4em' : '0.7em' }}>
          {line.text}
        </div>
      );
    }
  });

  return (
    <div className="dev-terminal-window">
      {/* macOS dots */}
      <div className="dev-terminal-titlebar">
        <div className="dev-terminal-dots">
          <span className="dev-dot dev-dot-red" />
          <span className="dev-dot dev-dot-yellow" />
          <span className="dev-dot dev-dot-green" />
        </div>
        <span className="dev-terminal-title">apetechs — bash</span>
      </div>
      <div className="dev-terminal-body" ref={termBodyRef}>
        {renderedLines}
      </div>
    </div>
  );
};

const BannerSection: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const { t } = useTranslation('home');
  const [appModalOpen, setAppModalOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="dev-hero-section">
      {/* Dot grid background */}
      <div className="dev-hero-dotgrid" />
      {/* Ambient glows */}
      <div className="dev-hero-glow dev-hero-glow-1" />
      <div className="dev-hero-glow dev-hero-glow-2" />

      <div className="dev-hero-inner">
        {/* LEFT CONTENT */}
        <div className={`dev-hero-left${mounted ? " dev-hero-visible" : ""}`}>
          <div className="dev-hero-badge">
            <span className="dev-hero-badge-dot" />
            {t('hero.badge')}
          </div>

          <h1 className="dev-hero-title">
            {t('hero.title_1')}<br />
            <span className="dev-hero-title-amber">{t('hero.title_2')}</span><br />
            {t('hero.title_3')}<br />
            <span style={{ color: "#94A3B8", fontSize: "0.72em" }}>{t('hero.title_4')}</span>
          </h1>

          <p className="dev-hero-subtitle">
            {t('hero.subtitle')}
          </p>

          {/* Module chips */}
          <div className="dev-hero-chips">
            {SYSTEMS_CHIPS.map((chip) => (
              <span key={chip} className="dev-hero-chip">{chip}</span>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="dev-hero-ctas">
            <button
              className="dev-cta-primary"
              onClick={() => setAppModalOpen(true)}
            >
              <span>▶</span> {t('hero.cta_primary')}
            </button>
            <button
              className="dev-cta-secondary"
              onClick={() => navigate("/contact")}
            >
              [ {t('hero.cta_secondary')} ]
            </button>
          </div>
        </div>

        {/* RIGHT TERMINAL */}
        {!isMobile && (
          <div className={`dev-hero-right${mounted ? " dev-hero-visible" : ""}`} style={{ transitionDelay: "0.2s" }}>
            <TerminalWindow />
          </div>
        )}
      </div>

      <AppModal visible={appModalOpen} onClose={() => setAppModalOpen(false)} />
    </section>
  );
};

export default BannerSection;
